import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
} from "react";

import Layout from "../components/ProductListLayout/Layout";
import SortandFilter from "../components/SortandFilter/SortandFilter";

import { useSelector, useDispatch } from "react-redux";

import {
    clearTags,
    setCategory,
} from "../../src/appState/slices/auth/authSlice";

import {
    getFilterProducts,
    getSaleProducts,
    getCategoryDetails,
} from "../appState/slices/product/productSlice";

import { useParams } from "react-router-dom";

import Empty from "../components/EmptyComponent/Empty";

const ProductList = () => {
    const dispatch = useDispatch();

    // --------------------------------------------------
    // PRODUCTS
    // --------------------------------------------------

    const products = useSelector(
        (state) => state.product.products
    );

    // --------------------------------------------------
    // FILTERS
    // --------------------------------------------------

    const selectedsizes = useSelector(
        (state) => state.auth.category.selectedsizes
    );

    const selectedcolors = useSelector(
        (state) => state.auth.category.selectedcolors
    );

    const sortby = useSelector(
        (state) => state.auth.category.sortby
    );

    const selectedsubcategory = useSelector(
        (state) => state.auth.category.subcategory
    );

    const minValue = useSelector(
        (state) => state.auth.category.minValue
    );

    const maxValue = useSelector(
        (state) => state.auth.category.maxValue
    );

    const tags = useSelector(
        (state) => state.auth.tags
    );

    // --------------------------------------------------
    // PAGINATION INFO FROM REDUX
    // --------------------------------------------------

    const pageInfo = useSelector(
        (state) => state.product.pageInfo
    );

    // --------------------------------------------------
    // ROUTE PARAMS
    // --------------------------------------------------

    const params = useParams();

    const { gender } = params;
    const { category: categoryName } = params;
    const { subcategory: subCategoryName } = params;
    const { discount } = params;
    const { collection } = params;

    // --------------------------------------------------
    // LOCAL PAGINATION STATE
    // --------------------------------------------------

    const [page, setPage] = useState(1);

    const [hasMore, setHasMore] = useState(true);

    const [loadingMore, setLoadingMore] = useState(false);

    // --------------------------------------------------
    // REFS
    // --------------------------------------------------

    // Prevent multiple requests for the same next page
    const loadingMoreRef = useRef(false);

    // Element at the bottom of the product list
    const observerRef = useRef(null);

    // --------------------------------------------------
    // CATEGORY DETAILS
    // --------------------------------------------------

    useEffect(() => {
        dispatch(
            getCategoryDetails({
                category: subCategoryName
                    ? subCategoryName
                    : null,

                collectionType: collection
                    ? collection
                    : null,

                gender: gender,
            })
        );
    }, [
        dispatch,
        selectedsubcategory,
        collection,
        subCategoryName,
        gender,
    ]);

    // --------------------------------------------------
    // CLEAR TAGS ON UNMOUNT
    // --------------------------------------------------

    useEffect(() => {
        return () => {
            dispatch(clearTags());
        };
    }, [dispatch]);

    // --------------------------------------------------
    // FETCH PRODUCTS
    // --------------------------------------------------

    const fetchProducts = useCallback(
        async (
            pageNumber = 1,
            append = false
        ) => {
            // ------------------------------------------
            // PREVENT DUPLICATE NEXT PAGE REQUESTS
            // ------------------------------------------

            if (
                append &&
                loadingMoreRef.current
            ) {
                return;
            }

            // ------------------------------------------
            // START BOTTOM LOADER
            // ------------------------------------------

            if (append) {
                loadingMoreRef.current = true;
                setLoadingMore(true);
            }

            try {
                // --------------------------------------
                // FETCH PRODUCTS
                // --------------------------------------

                const result = await dispatch(
                    getFilterProducts({
                        // Route filters
                        gender: gender,
                        isActive : true,
                        category: subCategoryName,

                        collectionType: collection
                            ? collection
                            : undefined,

                        // Product filters
                        sizes: selectedsizes,

                        colors: selectedcolors,

                        minPrice: minValue,

                        maxPrice: maxValue,

                        sortBy: sortby,

                        tags: tags,

                        // Pagination
                        page: pageNumber,

                        limit: 15,

                        // Tell Redux whether to replace
                        // or append products
                        append: append,
                    })
                ).unwrap();


                // --------------------------------------
                // PAGINATION INFO
                // --------------------------------------

                const currentPage =
                    Number(result?.page) ||
                    pageNumber;

                const totalPages =
                    Number(result?.pages) || 1;

                setPage(currentPage);

                setHasMore(
                    currentPage < totalPages
                );
            } catch (error) {
                console.error(
                    "Error fetching products:",
                    error
                );
            } finally {
                // --------------------------------------
                // STOP BOTTOM LOADER
                // --------------------------------------

                if (append) {
                    loadingMoreRef.current = false;
                    setLoadingMore(false);
                }
            }
        },
        [
            dispatch,
            gender,
            subCategoryName,
            collection,
            selectedsizes,
            selectedcolors,
            minValue,
            maxValue,
            sortby,
            tags,
        ]
    );

    // --------------------------------------------------
    // INITIAL LOAD / FILTER CHANGE
    // --------------------------------------------------

    useEffect(() => {
        // ----------------------------------------------
        // NORMAL CATEGORY / COLLECTION PRODUCTS
        // ----------------------------------------------

        if (categoryName || collection) {
            setPage(1);
            setHasMore(true);

            // Reset loading state in case route/filter
            // changes while another request is running
            loadingMoreRef.current = false;
            setLoadingMore(false);

            // Fetch first page and REPLACE products
            fetchProducts(1, false);
        }

        // ----------------------------------------------
        // SALE PRODUCTS
        // ----------------------------------------------

        if (discount) {
            dispatch(getSaleProducts());
        }
    }, [
        categoryName,
        collection,
        discount,

        tags,
        selectedsubcategory,

        gender,

        selectedsizes,
        selectedcolors,

        minValue,
        maxValue,

        sortby,

        fetchProducts,
        dispatch,
    ]);

    // --------------------------------------------------
    // INFINITE SCROLL
    // --------------------------------------------------

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];

                // Sentinel is not visible
                if (!entry.isIntersecting) {
                    return;
                }

                // --------------------------------------
                // GET CURRENT PAGE
                // --------------------------------------

                const currentPage =
                    Number(pageInfo?.page) ||
                    page;

                // --------------------------------------
                // GET TOTAL PAGES
                // --------------------------------------

                const totalPages =
                    Number(pageInfo?.pages) || 0;

                // --------------------------------------
                // ALREADY LOADING
                // --------------------------------------

                if (loadingMoreRef.current) {
                    return;
                }

                // --------------------------------------
                // NO MORE PRODUCTS
                // --------------------------------------

                if (
                    !hasMore ||
                    (
                        totalPages > 0 &&
                        currentPage >= totalPages
                    )
                ) {
                    return;
                }

                // --------------------------------------
                // FETCH NEXT PAGE
                // --------------------------------------

                fetchProducts(
                    currentPage + 1,
                    true
                );
            },
            {
                // Start loading slightly before
                // the user actually reaches the bottom
                rootMargin: "300px",
            }
        );

        const currentElement =
            observerRef.current;

        if (currentElement) {
            observer.observe(currentElement);
        }

        // ----------------------------------------------
        // CLEANUP
        // ----------------------------------------------

        return () => {
            if (currentElement) {
                observer.unobserve(
                    currentElement
                );
            }

            observer.disconnect();
        };
    }, [
        page,
        pageInfo?.page,
        pageInfo?.pages,
        hasMore,
        fetchProducts,
    ]);

    // --------------------------------------------------
    // UI
    // --------------------------------------------------

    return (
        <div className="productlistlayout">

            {/* ---------------------------------------- */}
            {/* SORT + FILTER */}
            {/* ---------------------------------------- */}

            <div className="sortandfiltertabs">
                <SortandFilter />
            </div>

            {/* ---------------------------------------- */}
            {/* PRODUCT LIST */}
            {/* ---------------------------------------- */}

            <div className="listlayout">

                {products.length ? (
                    <>
                        {/* -------------------------------- */}
                        {/* PRODUCTS */}
                        {/* -------------------------------- */}

                        <Layout
                            products={products}
                        />

                        {/* -------------------------------- */}
                        {/* INFINITE SCROLL TRIGGER */}
                        {/* -------------------------------- */}

                        <div
                            ref={observerRef}
                            style={{
                                height: "1px",
                                width: "100%",
                            }}
                        />

                        {/* -------------------------------- */}
                        {/* LOADING MORE */}
                        {/* -------------------------------- */}

                        {loadingMore && (
                            <div className="loading-more">
                                <div className="loading-spinner"></div>

                                <span>
                                    Loading more products...
                                </span>
                            </div>
                        )}

                    </>
                ) : (
                    /* ---------------------------------- */
                    /* EMPTY STATE */
                    /* ---------------------------------- */

                    <div className="productlist-empty">

                        <Empty
                            icon="SmileySad"
                            title="No Products Found"
                            subtitle="Sorry we were not able to find what you are looking for."
                        />

                    </div>
                )}

            </div>
        </div>
    );
};

export default ProductList;